import { useFiltersFactory, sort, array } from '@/utils';
import { Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { buildCategoryPath } from '../Categories.Common';
import { BaselinePersonalizationTableRow } from './BaselinePersonalizationTablePanel';
import { useMemo } from 'react';
import { ColorMarker, DateTimeValue, TableColumnType } from '@/components';
import { SpecialRequestTooltip } from '@/views';

export function useBaselinePersonalizationColumns(useNumbers: boolean) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'personalization.summary',
  });

  const filters = useFiltersFactory(
    array.empty<BaselinePersonalizationTableRow>(),
  );

  return useMemo<TableColumnType<BaselinePersonalizationTableRow>[]>(() => {
    const columns: Array<
      TableColumnType<BaselinePersonalizationTableRow> | false
    > = [
      {
        title: null,
        width: '5%',
        key: 'number',
        render: (_, { specialRequest, categoryPath }) =>
          specialRequest != null ? (
            <SpecialRequestTooltip />
          ) : (
            <ColorMarker color={categoryPath[0].hexColor} />
          ),
      },
      {
        title: t('project'),
        width: '5%',
        key: 'projectName',
        ...filters.plainText((x) => x.projectName),
        sorter: (a, b) => a.projectName.localeCompare(b.projectName),
        render: (_, { projectName }) => projectName,
      },
      useNumbers && {
        title: t('number'),
        width: '5%',
        key: 'number',
        sorter: (a, b) => a.number! - b.number!,
        render: (_, { number }) => number,
        defaultSortOrder: 'ascend',
      },

      {
        title: t('category'),
        key: 'category',
        width: '10%',
        ...filters.plainText((x) => buildCategoryPath(x.categoryPath)),
        sorter: (a, b) => {
          const pathA = buildCategoryPath(a.categoryPath);
          const pathB = buildCategoryPath(b.categoryPath);

          const comparison = pathA.localeCompare(pathB);

          if (comparison !== 0) {
            return comparison;
          }

          return pathA.length - pathB.length;
        },
        render: (_, { categoryPath }) => buildCategoryPath(categoryPath),
        defaultSortOrder: useNumbers ? null : 'ascend',
      },
      {
        title: t('itemName'),
        key: 'itemName',
        width: '10%',
        ...filters.plainText((x) => x.name),
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (_, { name }) => name,
      },
      {
        title: t('description'),
        key: 'description',
        width: '10%',
        ...filters.plainText((x) => x.description),
        sorter: (a, b) =>
          sort(a.description ?? undefined, b.description ?? undefined),
        render: (_, item) => <div>{item.description}</div>,
      },
      {
        title: t('notes'),
        key: 'notes',
        width: '10%',
        ...filters.plainText((x) => x.personalizationItem?.notes),
        sorter: (a, b) =>
          sort(
            a.personalizationItem?.notes ?? undefined,
            b.personalizationItem?.notes ?? undefined,
          ),
        render: (_, item) => <div>{item.personalizationItem?.notes}</div>,
      },
      {
        title: t('color'),
        key: 'subOption',
        width: '10%',
        ...filters.plainText((x) => x.personalizationItem?.subOption?.name),
        sorter: (a, b) =>
          sort(
            a.personalizationItem?.subOption?.name,
            b.personalizationItem?.subOption?.name,
          ),
        render: (_, item) => (
          <Space>{item.personalizationItem?.subOption?.name}</Space>
        ),
        className: 'text-end',
      },

      {
        title: t('createdAt'),
        key: 'createdAt',
        width: '10%',
        sorter: (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        ...filters.dateTimeRange((x) => x.createdAt),
        render: (_, item) => <DateTimeValue value={item.createdAt} />,
        className: 'text-end',
      },
    ];

    return columns.filter(
      (x) => !!x,
    ) as TableColumnType<BaselinePersonalizationTableRow>[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, useNumbers]);
}
